import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  Activity,
  BarChart3,
  Bell,
  BookUser,
  Briefcase,
  Building2,
  CalendarDays,
  CalendarPlus,
  ClipboardList,
  Eye,
  FileText,
  History,
  KeyRound,
  Kanban,
  LayoutDashboard,
  Library,
  ListChecks,
  LogOut,
  MessageSquare,
  Package,
  Search,
  Settings,
  Shield,
  Sparkles,
  UserCog,
  Video,
  Wallet,
} from "lucide-react";
import { allowedCreativeDepts } from "@/lib/creative-production";

import { supabase } from "@/integrations/supabase/client";
import { useAuth, DEPARTMENT_LABELS } from "@/lib/auth-context";
import {
  modCanWorkspace,
  modSectionEnabled,
  sectionForPath,
  type ModeratorPermissions,
} from "@/lib/moderator-permissions";
import {
  resolveScaffoldRole,
  sidebarConfig,
  type NavGroup,
  type NavItem,
} from "@/lib/role-scaffold";
import { cn } from "@/lib/utils";

const ACCORDION_STORAGE_PREFIX = "admin-sidebar-group:";

function CollapsibleGroup({
  label,
  children,
  hasActive,
  idx,
  collapsed = false,
}: {
  label: string;
  children: ReactNode;
  hasActive: boolean;
  idx: number;
  collapsed?: boolean;
}) {
  const storageKey = `${ACCORDION_STORAGE_PREFIX}${label}`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem(storageKey);
    return v === null ? true : v === "1";
  });
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">(open ? "auto" : 0);
  const firstRun = useRef(true);

  // Auto-open if active route is inside
  useEffect(() => {
    if (hasActive && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActive]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(storageKey, open ? "1" : "0");
    }
    const el = innerRef.current;
    if (!el) return;
    if (firstRun.current) {
      firstRun.current = false;
      setHeight(open ? "auto" : 0);
      return;
    }
    if (open) {
      setHeight(el.scrollHeight);
      const t = window.setTimeout(() => setHeight("auto"), 450);
      return () => window.clearTimeout(t);
    } else {
      setHeight(el.scrollHeight);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHeight(0));
      });
    }
  }, [open, storageKey]);

  if (collapsed) {
    // Rail mode: no header, thin divider between groups, render children directly.
    return (
      <div
        className={cn(
          "px-2",
          idx === 0 ? "pt-1" : "mt-3 pt-3 border-t border-border",
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div className={cn(idx === 0 ? "px-3" : "mt-4 px-3")}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mb-1 flex w-full items-center justify-between px-2 text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground hover:text-foreground transition-colors"
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform duration-300",
            open && "rotate-180",
          )}
        />
      </button>
      <div
        style={{
          height: typeof height === "number" ? `${height}px` : "auto",
          overflow: "hidden",
          transition: "height 450ms ease",
        }}
      >
        <div ref={innerRef}>{children}</div>
      </div>
    </div>
  );
}

export function AppSidebar({ forceVisible = false }: { forceVisible?: boolean } = {}) {
  const { profile, role, signOut } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });

  const initial = (profile?.full_name || profile?.email || "U").charAt(0).toUpperCase();
  const deptLabel = profile ? DEPARTMENT_LABELS[profile.department] : "";
  const isModerator = role === "moderator";
  const modPerms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;

  const scaffold = resolveScaffoldRole({
    appRole: role,
    department: profile?.department ?? null,
  });
  const groups: NavGroup[] = sidebarConfig[scaffold] ?? [];

  const isAdmin = role === "admin";
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("admin-sidebar-collapsed") === "1";
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("admin-sidebar-collapsed", collapsed ? "1" : "0");
    }
  }, [collapsed]);
  // Rail mode only applies for admin on desktop (not in mobile drawer).
  const railMode = isAdmin && !forceVisible && collapsed;

  // Chat badge = number of ROOMS with ≥1 unread message for me.
  const currentUserId = profile?.id;
  const [unreadRooms, setUnreadRooms] = useState(0);
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    const compute = async () => {
      const { data: mems } = await supabase
        .from("chat_members")
        .select("room_id, last_read_at, joined_at")
        .eq("user_id", currentUserId);
      if (cancelled) return;
      if (!mems || mems.length === 0) {
        setUnreadRooms(0);
        return;
      }
      const roomIds = (mems as { room_id: string }[]).map((m) => m.room_id);
      const lr: Record<string, string> = {};
      (mems as { room_id: string; last_read_at: string | null; joined_at: string }[]).forEach(
        (m) => {
          lr[m.room_id] = m.last_read_at ?? m.joined_at;
        },
      );
      const { data: msgs } = await supabase
        .from("chat_messages")
        .select("room_id, sender_id, created_at")
        .in("room_id", roomIds)
        .order("created_at", { ascending: false })
        .limit(1000);
      if (cancelled) return;
      const unread = new Set<string>();
      (msgs ?? []).forEach((m: { room_id: string; sender_id: string; created_at: string }) => {
        if (m.sender_id === currentUserId) return;
        const ts = lr[m.room_id];
        if (ts && new Date(m.created_at).getTime() > new Date(ts).getTime()) {
          unread.add(m.room_id);
        }
      });
      setUnreadRooms(unread.size);
    };
    compute();
    const channel = supabase
      .channel(`sidebar-unread-${currentUserId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        () => compute(),
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "chat_members",
          filter: `user_id=eq.${currentUserId}`,
        },
        () => compute(),
      )
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "chat_members",
          filter: `user_id=eq.${currentUserId}`,
        },
        () => compute(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [currentUserId]);

  // Moderator extras: Team View items they have caps for, plus Audit Log if granted.
  const moderatorExtras: NavGroup[] = [];
  if (isModerator) {
    const teamItems: NavItem[] = [];
    if (modCanWorkspace(modPerms, "view_overview"))
      teamItems.push({ to: "/team/overview", label: "Overview", icon: Eye });
    if (
      Object.values(modPerms.departments ?? {}).some((d) =>
        Object.values(d ?? {}).some(Boolean),
      )
    )
      teamItems.push({ to: "/team/departments", label: "By Department", icon: Building2 });
    if (modCanWorkspace(modPerms, "search_users"))
      teamItems.push({ to: "/team/employees", label: "By Employee", icon: Search });
    if (modCanWorkspace(modPerms, "view_activity_feed"))
      teamItems.push({ to: "/team/activity", label: "Activity Feed", icon: Activity });
    if (teamItems.length) {
      teamItems.unshift({ to: "/today-tasks", label: "Today's Task", icon: ClipboardList });
      moderatorExtras.push({ label: "Team View", items: teamItems });
    }

    if (modCanWorkspace(modPerms, "view_audit_log")) {
      moderatorExtras.push({
        label: "Audit",
        items: [{ to: "/admin/audit-log", label: "Audit Log", icon: History }],
      });
    }

    if (modCanWorkspace(modPerms, "creatives_review")) {
      moderatorExtras.push({
        label: "Review",
        items: [{ to: "/creatives", label: "Creatives", icon: Sparkles }],
      });
    }

    if (modCanWorkspace(modPerms, "task_assign")) {
      moderatorExtras.push({
        label: "Tasks",
        items: [{ to: "/admin/assign-task", label: "Assign Task", icon: ClipboardList }],
      });
    }
  }

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const active = path === item.to || path.startsWith(item.to + "/");
    const isChat = item.to === "/chat";
    const hasBadge = isChat && unreadRooms > 0;
    return (
      <Link
        key={item.to}
        to={item.to}
        title={railMode ? item.label : undefined}
        className={cn(
          "relative flex items-center rounded-md py-[7px] text-[11px] transition-colors",
          railMode ? "justify-center px-0" : "gap-2 px-2",
          active
            ? "font-semibold text-foreground before:absolute before:left-[-4px] before:top-1 before:bottom-1 before:w-[2px] before:rounded-full before:bg-[#7C3AED]"
            : "font-medium text-muted-foreground hover:bg-sidebar-accent",
        )}
      >
        <span className="relative inline-flex">
          <Icon className={cn("h-[14px] w-[14px]", active && "text-[#7C3AED]")} />
          {railMode && hasBadge && (
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#7C3AED]" />
          )}
        </span>
        {!railMode && <span>{item.label}</span>}
        {!railMode && hasBadge && (
          <span className="ml-auto h-4 min-w-4 rounded-full bg-[#7C3AED] text-white text-[9px] font-semibold flex items-center justify-center px-1">
            {unreadRooms > 9 ? "9+" : unreadRooms}
          </span>
        )}
      </Link>
    );
  };

  const renderGroup = (g: NavGroup, idx: number) => {
    if (railMode) {
      return (
        <div
          key={`${g.label}-${idx}`}
          className={cn("px-2", idx === 0 ? "pt-1" : "mt-3 pt-3 border-t border-border")}
        >
          <nav className="flex flex-col gap-1.5">{g.items.map(renderItem)}</nav>
        </div>
      );
    }
    return (
      <div key={`${g.label}-${idx}`} className={cn(idx === 0 ? "px-3" : "mt-4 px-3")}>
        <div className="mb-1 px-2 text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
          {g.label}
        </div>
        <nav className="flex flex-col gap-1.5">{g.items.map(renderItem)}</nav>
      </div>
    );
  };

  // Notifications: admin always, HR always, moderator if send_notifications.
  const canSendNotifications =
    role === "admin" ||
    profile?.department === "hr" ||
    (isModerator && Boolean(modPerms.send_notifications));
  const notificationsGroup: NavGroup[] = canSendNotifications
    ? [
        {
          label: "Notifications",
          items: [
            { to: "/notifications/send", label: "Send", icon: Bell },
            { to: "/notifications/history", label: "History", icon: History },
          ],
        },
      ]
    : [];

  // Leave: every employee (user, moderator, admin) except HR dept (HR manages via /hr/leave).
  const showLeave = profile?.department !== "hr";
  const leaveGroup: NavGroup[] = showLeave
    ? [
        {
          label: "Leave",
          items: [
            { to: "/leave/apply", label: "Apply Leave", icon: CalendarPlus },
            { to: "/leave/my-leaves", label: "My Leaves", icon: CalendarDays },
          ],
        },
      ]
    : [];

  const chatGroup: NavGroup[] = [
    {
      label: "Messaging",
      items: [{ to: "/chat", label: "Chat", icon: MessageSquare }],
    },
  ];

  // Creative Library: visible to admin, moderator, editor, marketer, scriptwriter.
  const creativeLibraryAllowed =
    role === "admin" ||
    role === "moderator" ||
    profile?.department === "editor" ||
    profile?.department === "marketer" ||
    profile?.department === "scriptwriter";
  const contentGroup: NavGroup[] = creativeLibraryAllowed
    ? [
        {
          label: "Content",
          items: [{ to: "/creative-library", label: "Creative Library", icon: Library }],
        },
      ]
    : [];

  // Ads Performance: gated via ads_section_permissions table.
  const adsRoleKey =
    role === "admin"
      ? "admin"
      : role === "moderator"
        ? "moderator"
        : profile?.department === "graphic_designer"
          ? "designer"
          : (profile?.department ?? null);
  const [adsAllowed, setAdsAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!adsRoleKey) {
      setAdsAllowed(false);
      return;
    }
    const refetch = async () => {
      const { data } = await supabase
        .from("ads_section_permissions")
        .select("can_access")
        .eq("role", adsRoleKey)
        .maybeSingle();
      if (!cancelled) setAdsAllowed(!!data?.can_access);
    };
    refetch();
    const channel = supabase
      .channel(`ads-perm-${adsRoleKey}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "ads_section_permissions",
          filter: `role=eq.${adsRoleKey}`,
        },
        () => refetch(),
      )
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [adsRoleKey]);

  // Meta IDs Manager — visible only to admin + explicitly granted users.
  const [metaIdsAllowed, setMetaIdsAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!profile?.id) {
      setMetaIdsAllowed(false);
      return;
    }
    (async () => {
      const { data } = await supabase.rpc("current_can_access_meta_ids");
      if (!cancelled) setMetaIdsAllowed(!!data);
    })();
    return () => { cancelled = true; };
  }, [profile?.id]);
  const performanceGroup: NavGroup[] = adsAllowed
    ? [
        {
          label: "Performance",
          items: [{ to: "/ads-performance", label: "Ads Performance", icon: BarChart3 }],
        },
      ]
    : [];

  const kanbanGroup: NavGroup[] = [
    {
      label: "Task Management",
      items: [
        { to: "/boards", label: "Boards", icon: Kanban },
      ],
    },
  ];

  const sopsGroup: NavGroup[] = [
    {
      label: "SOPs",
      items: [{ to: "/sops", label: "SOPs", icon: FileText }],
    },
  ];

  // Admin-specific structure: one standalone "Home" + 6 groups.
  const adminHomeItem: NavItem = { to: "/dashboard", label: "Home", icon: LayoutDashboard };
  const adminGroups: NavGroup[] = [
    {
      label: "Work",
      items: [
        { to: "/update-task", label: "Work Update", icon: ClipboardList },
        { to: "/boards", label: "Work Boards", icon: Kanban },
        { to: "/calendar", label: "Calendar", icon: CalendarDays },
        { to: "/creatives", label: "Creatives", icon: Sparkles },
        { to: "/creative-library", label: "Creative Library", icon: Library },
      ],
    },
    {
      label: "Team Performance",
      items: [
        { to: "/today-tasks", label: "Today's Work", icon: ListChecks },
        { to: "/creative-production", label: "Creative Production", icon: Video },
        { to: "/team/departments", label: "By Department", icon: Building2 },
        { to: "/team/employees", label: "By Employee", icon: Search },
      ],
    },

    {
      label: "HR & Operations",
      items: [
        { to: "/hr/directory", label: "All Employees", icon: BookUser },
        { to: "/hr/recruitment", label: "Recruitment", icon: Briefcase },
        { to: "/hr/payroll", label: "Payroll", icon: Wallet },
        { to: "/hr/assets", label: "Allot Assets", icon: Package },
        { to: "/hr/documents", label: "Documents", icon: FileText },
        { to: "/hr/leave", label: "Leave Requests", icon: CalendarDays },
        { to: "/hr/exit-reviews", label: "Exit Interviews", icon: FileText },
      ],
    },
    {
      label: "Communications",
      items: [
        { to: "/chat", label: "Chat", icon: MessageSquare },
        { to: "/notifications/send", label: "Send", icon: Bell },
        { to: "/notifications/history", label: "History", icon: History },
      ],
    },
    {
      label: "The Vault",
      items: [
        { to: "/vault", label: "The Vault", icon: KeyRound },
        ...(metaIdsAllowed ? [{ to: "/meta-ids", label: "Meta IDs Manager", icon: Shield }] : []),
      ],
    },
  ];

  const metaIdsGroup: NavGroup[] = metaIdsAllowed
    ? [{ label: "The Vault", items: [{ to: "/meta-ids", label: "Meta IDs Manager", icon: Shield }] }]
    : [];

  // Creative Production — HR, team leads and permitted moderators (admins get it
  // inside their own "Team Performance" group above).
  const creativeProdGroup: NavGroup[] =
    allowedCreativeDepts(role, profile).length > 0
      ? [
          {
            label: "Team Performance",
            items: [
              { to: "/creative-production", label: "Creative Production", icon: Video },
            ],
          },
        ]
      : [];

  const rawAllGroups = isAdmin
    ? adminGroups
    : [
        ...groups,
        ...moderatorExtras,
        ...creativeProdGroup,
        ...kanbanGroup,

        ...sopsGroup,
        ...contentGroup,
        ...performanceGroup,
        ...chatGroup,
        ...notificationsGroup,
        ...leaveGroup,
        ...metaIdsGroup,
      ];

  // For moderators, hide items whose section toggle is explicitly disabled.
  const allGroups: NavGroup[] = isModerator
    ? rawAllGroups
        .map((g) => ({
          ...g,
          items: g.items.filter((it) => {
            const sec = sectionForPath(it.to);
            return sec ? modSectionEnabled(modPerms, sec) : true;
          }),
        }))
        .filter((g) => g.items.length > 0)
    : rawAllGroups;

  return (
    <aside
      className={cn(
        "flex-col border-r border-border bg-sidebar h-full min-h-0 overflow-hidden transition-[width] duration-300 ease-out",
        railMode ? "w-[64px]" : "w-[200px]",
        forceVisible ? "flex" : "hidden md:flex",
      )}
    >
      {/* Brand */}
      <div
        className={cn(
          "flex shrink-0 items-center pt-4 pb-3",
          railMode ? "flex-col gap-2 px-2" : "gap-2 px-3",
        )}
      >
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#18181B] text-[10px] font-extrabold text-white">
          CP
        </div>
        {!railMode && (
          <div className="leading-tight flex-1 min-w-0">
            <div className="text-[13px] font-bold">CretikPanda</div>
            <div className="text-[9px] text-muted-foreground">Workspace</div>
          </div>
        )}
        {isAdmin && !forceVisible && (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-foreground transition-colors",
              !railMode && "ml-auto",
            )}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-[14px] w-[14px]" />
            ) : (
              <PanelLeftClose className="h-[14px] w-[14px]" />
            )}
          </button>
        )}
      </div>

      {/* Profile card */}
      <div
        className={cn(
          "mb-3 shrink-0 rounded-lg border border-border bg-background",
          railMode ? "mx-2 p-1" : "mx-3 p-2",
        )}
      >
        <div className={cn("flex items-center", railMode ? "justify-center" : "gap-2")}>
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-[12px] font-bold text-white"
            style={{ background: "var(--gradient-avatar)" }}
            title={railMode ? profile?.full_name || "" : undefined}
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
            ) : (
              initial
            )}
          </div>
          {!railMode && (
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[11px] font-semibold">{profile?.full_name || "—"}</div>
              <div className="truncate text-[9px] text-muted-foreground">
                {deptLabel} · {role ?? ""}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {isAdmin ? (
          <>
            <div className={railMode ? "px-2" : "px-3"}>
              <nav className="flex flex-col gap-1.5">{renderItem(adminHomeItem)}</nav>
            </div>
            {adminGroups.map((g, i) => {
              // Keep "The Vault" as a non-collapsible group exactly as it is.
              if (g.label === "The Vault") {
                return renderGroup(g, i + 1);
              }
              const hasActive = g.items.some(
                (it) => path === it.to || path.startsWith(it.to + "/"),
              );
              return (
                <CollapsibleGroup
                  key={g.label}
                  label={g.label}
                  idx={i + 1}
                  hasActive={hasActive}
                  collapsed={railMode}
                >
                  <nav className="flex flex-col gap-1.5">{g.items.map(renderItem)}</nav>
                </CollapsibleGroup>
              );
            })}
            {/* SOPs — everyone (admin standalone slot) */}
            <div className={railMode ? "mt-2 px-2" : "mt-2 px-3"}>
              <nav className="flex flex-col gap-1.5">
                {renderItem({ to: "/sops", label: "SOPs", icon: FileText })}
              </nav>
            </div>
            {/* Settings — admin only */}
            <div className={railMode ? "px-2" : "px-3"}>
              <nav className="flex flex-col gap-1.5">
                {renderItem({ to: "/admin/settings", label: "Settings", icon: Settings })}
              </nav>
            </div>
            <CollapsibleGroup
              label="Account"
              idx={adminGroups.length + 1}
              hasActive={path === "/settings"}
              collapsed={railMode}
            >
              <nav className="flex flex-col gap-1.5">
                <Link
                  to="/settings"
                  title={railMode ? "Profile & Password" : undefined}
                  className={cn(
                    "relative flex items-center rounded-md py-[7px] text-[11px] transition-colors",
                    railMode ? "justify-center px-0" : "gap-2 px-2",
                    path === "/settings"
                      ? "font-semibold text-foreground before:absolute before:left-[-4px] before:top-1 before:bottom-1 before:w-[2px] before:rounded-full before:bg-[#7C3AED]"
                      : "font-medium text-muted-foreground hover:bg-sidebar-accent",
                  )}
                >
                  <UserCog
                    className={cn("h-[14px] w-[14px]", path === "/settings" && "text-[#7C3AED]")}
                  />
                  {!railMode && <span>Profile & Password</span>}
                </Link>
                <button
                  onClick={() => signOut()}
                  title={railMode ? "Logout" : undefined}
                  className={cn(
                    "flex items-center rounded-md py-[7px] text-[11px] font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent",
                    railMode ? "justify-center px-0" : "gap-2 px-2",
                  )}
                >
                  <LogOut className="h-[14px] w-[14px]" />
                  {!railMode && <span>Logout</span>}
                </button>
              </nav>
            </CollapsibleGroup>
            <div className="h-4" />
          </>
        ) : (
          <>
            {allGroups.map((g, i) => renderGroup(g, i))}
          </>
        )}
      </div>


      {/* ACCOUNT (non-admin only — admin Account is an accordion above) */}
      {!isAdmin && (
        <div className="shrink-0 border-t border-border px-3 pt-3 pb-4">
          <div className="mb-1 px-2 text-[9px] font-semibold uppercase tracking-[0.5px] text-muted-foreground">
            Account
          </div>
          <nav className="flex flex-col gap-1.5">
            <Link
              to="/settings"
              className={cn(
                "relative flex items-center gap-2 rounded-md px-2 py-[7px] text-[11px] transition-colors",
                path === "/settings"
                  ? "font-semibold text-foreground before:absolute before:left-[-4px] before:top-1 before:bottom-1 before:w-[2px] before:rounded-full before:bg-[#7C3AED]"
                  : "font-medium text-muted-foreground hover:bg-sidebar-accent",
              )}
            >
              <Settings
                className={cn("h-[14px] w-[14px]", path === "/settings" && "text-[#7C3AED]")}
              />
              <span>Settings</span>
            </Link>
            <button
              onClick={() => signOut()}
              className="flex items-center gap-2 rounded-md px-2 py-[7px] text-[11px] font-medium text-muted-foreground transition-colors hover:bg-sidebar-accent"
            >
              <LogOut className="h-[14px] w-[14px]" />
              <span>Logout</span>
            </button>
          </nav>
        </div>
      )}
    </aside>
  );
}
